import { Button, Col, Image, Modal, Row } from 'react-bootstrap'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import RegularItem from './eventTypes/RegularItem';
import PathwayItem from './eventTypes/PathwayItem'
import PollItem from './eventTypes/PollItem';
import PollResultItem from './eventTypes/PollResultItem';
import EmbeddedNavigationView from 'components/navigation/EmbeddedNavigationView';
import { getSocket } from '../../../socket';
import { resolveAvatarSrc } from 'utils/avatar';
import { getTimerColors } from 'utils/timerColors';
import { getActivityTimingLimits } from 'utils/activityTiming';
import Limits from './eventTypes/SharedComponents/Limits';
import { getPathwayTitle } from 'utils/pathwayTitle';
import { isOfficer } from 'utils/acl';
import AgendaItemTimerControls from '../AgendaItemTimerControls';

export default function AgendaItemModal({ agenda, item, getMeeting, meeting, showModal, handleCloseModal, showClockText = true, timerDisplayEnabled = true, remoteControlled = false, navigationParent = null }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const embedded = Boolean(navigationParent);
  // console.log('AgendaItemModal mounted');

  // Provide swipe handlers as props to event components (must be after state/props)

  function getSwipeHandlers() {

    if (isPlaying || remoteControlled || embedded) return {};
    let startX = null;
    let startY = null;
    let isTouching = false;
    const minSwipeDistance = 50;
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length === 1) {
        isTouching = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        // setSwipeDirection(null);
      }
    };
    const handleTouchEnd = (e) => {
      if (!isTouching || startX === null) return;
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - startX;
      const dy = endY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > minSwipeDistance) {
        if (dx < 0 && currentIndex < agenda.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else if (dx > 0 && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
      }
      isTouching = false;
      startX = null;
      startY = null;
    };
    return {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd
    };
  }
  // Set currentIndex only when modal opens or item changes from parent
  const [currentIndex, setCurrentIndex] = useState(
    agenda.findIndex(agendaItem => agendaItem.agendaItemId === item.agendaItemId)
  );
  const prevShowModal = useRef(showModal);


  useEffect(() => {
    // Only reset currentIndex if modal just opened, or item changed while closed
    if (showModal && !prevShowModal.current) {
      // Modal just opened
      const idx = agenda.findIndex(agendaItem => agendaItem.agendaItemId === item.agendaItemId);
      setCurrentIndex(idx);
    } else if (!showModal && item) {
      // Modal is closed, update index for next open if item changes
      const idx = agenda.findIndex(agendaItem => agendaItem.agendaItemId === item.agendaItemId);
      setCurrentIndex(idx);
    }
    prevShowModal.current = showModal;
    // eslint-disable-next-line
  }, [showModal, item]);


  const pushedHistoryRef = useRef(false);
  const closingByButtonRef = useRef(false);

  // Intercept the browser back button to close the modal instead of navigating away
  useEffect(() => {
    if (!showModal || embedded) return;
    window.history.pushState({ agendaModalOpen: true }, '');
    pushedHistoryRef.current = true;

    const handlePopState = () => {
      if (!closingByButtonRef.current) {
        pushedHistoryRef.current = false;
        handleCloseModal();
        void Promise.resolve(getMeeting?.());
      }
      closingByButtonRef.current = false;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line
  }, [showModal, embedded]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [timerControlsTarget, setTimerControlsTarget] = useState(null);
  const [mainDebateTime, setMainDebateTime] = useState(Number(item?.timer?.time || 0));
  const [debateEditing, setDebateEditing] = useState(false);
  const [isVisibilitySaving, setIsVisibilitySaving] = useState(false);
  const currentAgendaItem = agenda[currentIndex];
  const currentItem = useMemo(() => (
    remoteControlled
      ? { ...currentAgendaItem, hideTitleAndSubtitle: item?.hideTitleAndSubtitle === true }
      : currentAgendaItem
  ), [currentAgendaItem, item?.hideTitleAndSubtitle, remoteControlled]);
  const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {};
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.timerManager || '').trim())
  );
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked);
  const meetingNumber = meeting?.meetingDetails?.meetingNumber || currentItem?.meetingNumber;
  const meetingTitle = meeting?.meetingDetails?.meetingTheme || currentItem?.meetingTheme;
  const isDebate = currentItem?.activity?.type === 'debate';
  const debateTitle = getPathwayTitle(currentItem);
  const canEditDebate = Boolean(authUser?.token && !isMeetingLocked && !remoteControlled && (
    isTimerManager || isOfficer(authUser?.user?.acl || authUser?.acl || '')
    || authUser?.user?.isWebmaster || authUser?.isWebmaster
    || authIdentityIds.includes(String(currentItem?.assignedUser?.id || ''))
  ));
  const debateLimits = getActivityTimingLimits(currentItem?.activity);
  const debateTimerColors = isDebate && timerDisplayEnabled ? getTimerColors(
    mainDebateTime,
    debateLimits || currentItem?.agendaEvent,
    { background: 'var(--modal-bg)', foreground: 'var(--foreground)' }
  ) : null;
  const debateChromeStyle = debateTimerColors ? {
    background: debateTimerColors.background,
    color: debateTimerColors.foreground,
  } : undefined;

  useEffect(() => {
    setDebateEditing(false);
  }, [currentItem?.agendaItemId, showModal]);

  useEffect(() => {
    setMainDebateTime(Number(currentItem?.timer?.time || 0));
  }, [currentItem?.agendaItemId, currentItem?.timer?.time]);

  useEffect(() => {
    if (!showModal || !isDebate || !currentItem?.agendaItemId) return undefined;
    const currentMeetingId = meeting?.meetingDetails?.id || meeting?._id || currentItem?.meeting;
    if (!currentMeetingId) return undefined;
    const socket = getSocket();
    const matches = (payload) => !payload?.segmentId
      && String(payload?.meetingId || '') === String(currentMeetingId)
      && String(payload?.agendaItemId || '') === String(currentItem.agendaItemId);
    const onTick = (payload) => {
      if (!matches(payload)) return;
      const nextTime = Number(payload.time);
      if (Number.isFinite(nextTime)) setMainDebateTime(Math.max(0, nextTime));
    };
    const onState = (payload) => {
      if (!matches(payload)) return;
      const nextTime = Number(payload.time);
      if (Number.isFinite(nextTime)) setMainDebateTime(Math.max(0, nextTime));
    };
    const onReset = (payload) => {
      if (!matches(payload)) return;
      setMainDebateTime(0);
    };
    socket.on('meeting:timer-tick', onTick);
    socket.on('meeting:timer-state', onState);
    socket.on('meeting:timer-reset', onReset);
    socket.emit('meeting:join', String(currentMeetingId));
    return () => {
      socket.off('meeting:timer-tick', onTick);
      socket.off('meeting:timer-state', onState);
      socket.off('meeting:timer-reset', onReset);
    };
  }, [showModal, isDebate, currentItem?.agendaItemId, currentItem?.meeting, meeting]);

  const toggleTitleVisibility = async () => {
    if (!isTimerManager || !currentItem?.agendaItemId || isVisibilitySaving || isMeetingLocked) return;
    setIsVisibilitySaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/agendaItem/${currentItem.agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify({ hideTitleAndSubtitle: !currentItem.hideTitleAndSubtitle }),
      });
      if (!response.ok) throw new Error('Unable to update agenda title visibility.');
      await Promise.resolve(getMeeting?.());
    } catch (error) {
      window.alert(error.message || 'Unable to update agenda title visibility.');
    } finally {
      setIsVisibilitySaving(false);
    }
  };

  // Fetch meeting data when modal opens
  useEffect(() => {
    if (showModal && !remoteControlled) {
      Promise.resolve(getMeeting());
    }
    // eslint-disable-next-line
  }, [showModal]);

  // Log currentItem when modal opens or currentItem changes
  useEffect(() => {
    if (showModal && currentItem) {
      // console.log('currentItem:', currentItem);
    }
  }, [showModal, currentItem]);

  const handleNextItem = async () => {
    if (currentIndex < agenda.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePreviousItem = async () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Wrapper to refresh data and close modal
  const handleCloseAndRefresh = useCallback(() => {
    if (pushedHistoryRef.current) {
      closingByButtonRef.current = true;
      pushedHistoryRef.current = false;
      window.history.back();
    }
    handleCloseModal();
    void Promise.resolve(getMeeting?.());
  }, [getMeeting, handleCloseModal]);

  const renderEventComponent = () => {
    const swipeHandlers = getSwipeHandlers();
    switch (isDebate ? 'pathway' : currentItem?.agendaEvent?.eventType) {
      case 'regular':
        return (
          <RegularItem
            item={currentItem}
            getMeeting={getMeeting}
            meeting={meeting}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            showClockText={showClockText}
            timerDisplayEnabled={timerDisplayEnabled}
            allowTimerControls={timerDisplayEnabled}
            timerControlsTarget={timerControlsTarget}
            remoteControlled={remoteControlled}
            swipeHandlers={swipeHandlers}
          />
        );
        case 'pathway':
        return (
          <PathwayItem
            debateEditing={debateEditing}
            onDebateEditingChange={setDebateEditing}
            item={currentItem}
            getMeeting={getMeeting}
            meeting={meeting}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            showClockText={showClockText}
            timerDisplayEnabled={timerDisplayEnabled}
            allowTimerControls={timerDisplayEnabled}
            timerControlsTarget={timerControlsTarget}
            remoteControlled={remoteControlled}
            swipeHandlers={swipeHandlers}
          />
        );
      case 'poll':
        return (
          <PollItem
            agenda={agenda}
            item={currentItem}
            meeting={meeting}
            swipeHandlers={swipeHandlers}
            onPollClosed={handleCloseAndRefresh}
          />
        );
      case 'pollresult':
        return (
          <PollResultItem
            agenda={agenda}
            item={currentItem}
            meeting={meeting}
            isTimerManager={isTimerManager}
            swipeHandlers={swipeHandlers}
          />
        );
      default:
        return (
          <RegularItem
            item={currentItem}
            getMeeting={getMeeting}
            meeting={meeting}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            showClockText={showClockText}
            timerDisplayEnabled={timerDisplayEnabled}
            allowTimerControls={timerDisplayEnabled}
            timerControlsTarget={timerControlsTarget}
            remoteControlled={remoteControlled}
            swipeHandlers={swipeHandlers}
          />
        );
    }
  };

  const eventComponent = renderEventComponent();

  if (embedded) {
    return (
      <EmbeddedNavigationView
        parent={navigationParent}
        show={showModal}
        title={currentItem?.agendaEvent?.name || 'Voting'}
        onClose={handleCloseAndRefresh}
      >
        <div className="mobile-tool-body agenda-item-embedded">
          {eventComponent && (
            <fieldset disabled={isMeetingLocked} style={{ border: 'none', padding: 0, margin: 0 }}>
              {eventComponent}
            </fieldset>
          )}
        </div>
        <div className="mobile-tool-footer d-flex flex-wrap align-items-center justify-content-end gap-2 mt-3">
          <Button variant="dark" onClick={getMeeting} title="Reload agenda" aria-label="Reload agenda">
            <i className='bi bi-arrow-clockwise' aria-hidden='true'></i>
          </Button>
          <Button variant='success' onClick={handleCloseAndRefresh}>
            Close
          </Button>
        </div>
      </EmbeddedNavigationView>
    );
  }

  return (

    < Modal
      centered
      show={showModal}
      onHide={handleCloseAndRefresh}
      className={`text-center${remoteControlled ? ' remote-controlled-agenda-modal' : ''}`}
      fullscreen
    >
      <Modal.Header className='timer-header' style={debateChromeStyle} aria-label={isDebate ? 'Debate timer' : undefined}>
        <Row className='w-100'>
          <Col className='d-flex w-100 align-items-center justify-content-between gap-2'>
            <div className={`text-start${isDebate ? ' debate-header-content' : ''}`}>
              <h2 className={`mb-0${isDebate ? ' activity-debate-event-name' : ''}`}>{isDebate
                ? `DEBATE${!currentItem?.hideTitleAndSubtitle && debateTitle.speech ? ` - ${debateTitle.speech}` : ''}`
                : <>{meetingNumber && `#${meetingNumber} - `}{meetingTitle}</>}</h2>
              {isDebate && !currentItem?.hideTitleAndSubtitle && (
                <button type="button" className="debate-project-header" disabled={!canEditDebate}
                  aria-label="Edit debate speaker and pathway project" onClick={() => setDebateEditing(true)}>
                  {[debateTitle.pathway, debateTitle.project].filter(Boolean).join(' - ') || 'Choose pathway and project'}
                </button>
              )}
              {isDebate && (
                <>
                <div className='d-flex align-items-center gap-2 mt-1'>
                  {resolveAvatarSrc(currentItem?.assignedUser?.avatarUrl || currentItem?.assignedUser?.avatar || '') && (
                    <Image
                      src={resolveAvatarSrc(currentItem?.assignedUser?.avatarUrl || currentItem?.assignedUser?.avatar || '')}
                      roundedCircle
                      width={32}
                      height={32}
                      alt=""
                    />
                  )}
                  <small>
                    Moderator: {currentItem?.assignedUser?.firstName || 'TBA'} {currentItem?.assignedUser?.lastName || ''}
                  </small>
                  {timerDisplayEnabled && <div role="group" aria-label="Entire debate timer" className="mt-2">
                    <AgendaItemTimerControls key={currentItem?.agendaItemId}
                      item={{ ...currentItem, agendaEvent: debateLimits || currentItem?.agendaEvent }}
                      meeting={meeting} readOnly showClockText={showClockText}
                    />
                  </div>}
                </div>
                <Limits item={{ agendaEvent: debateLimits || currentItem?.agendaEvent }} readOnly compact />
                </>
              )}
            </div>
            <div className='d-flex align-items-center gap-1'>
              {isTimerManager && (
                <Button
                  variant="dark"
                  onClick={toggleTitleVisibility}
                  disabled={isVisibilitySaving || isMeetingLocked}
                  title={isMeetingLocked ? 'This meeting is locked' : currentItem?.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
                  aria-label={currentItem?.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
                >
                  <i className={`bi ${currentItem?.hideTitleAndSubtitle ? 'bi-eye' : 'bi-eye-slash'}`} aria-hidden='true'></i>
                </Button>
              )}
              {remoteControlled && (
                <>
                  <Button
                    variant="dark"
                    onClick={getMeeting}
                    title="Reload agenda"
                    aria-label="Reload agenda"
                  >
                    <i className='bi bi-arrow-clockwise' aria-hidden='true'></i>
                  </Button>
                  <Button
                    variant="dark"
                    onClick={handleCloseAndRefresh}
                    title="Close remote display"
                    aria-label="Close remote display"
                  >
                    <i className='bi bi-x-lg' aria-hidden='true'></i>
                  </Button>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Header>

      {eventComponent && (
        <fieldset disabled={isMeetingLocked} style={{ border: 'none', padding: 0, margin: 0, display: 'contents' }}>
          {eventComponent}
        </fieldset>
      )}

          <Modal.Footer className="agenda-modal-footer" style={debateChromeStyle} aria-label="Agenda controls">
            <>
              <div className="agenda-modal-footer-navigation" style={{ visibility: isPlaying ? 'hidden' : undefined }} aria-hidden={isPlaying}>
                {!remoteControlled && <>
                <Button
                  onClick={handlePreviousItem}
                  disabled={isPlaying || currentIndex === 0}
                >
                  <i className='bi bi-arrow-left'></i>
                </Button>
                <Button
                  onClick={handleNextItem}
                  disabled={isPlaying || currentIndex === agenda.length - 1}
                >
                  <i className='bi bi-arrow-right'></i>
                </Button>
                </>}
              </div>
              <div className="agenda-modal-footer-timer" ref={setTimerControlsTarget} />
              <div className="agenda-modal-footer-actions" style={{ visibility: isPlaying || remoteControlled ? 'hidden' : undefined }} aria-hidden={isPlaying || remoteControlled}>
                <Button variant="dark" onClick={getMeeting} disabled={isPlaying}>
                  <i className='bi bi-arrow-clockwise'></i>
                </Button>
                <Button variant='success' onClick={handleCloseAndRefresh} disabled={isPlaying}>
                  Close
                </Button>
              </div>
            </>
          </Modal.Footer>
    </Modal >
  )
}